import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import {
  CONDITION_LABEL,
  EQUIPMENT_STATUS_LABEL,
  equipmentLabel,
  FACILITY_SYSTEM_LABEL,
  type Equipment,
  type EquipmentDetail,
  type EquipmentInput,
  type EquipmentStatus,
  type FacilityCondition,
  type FacilitySystem,
} from '../lib/facilities';
import { PlaceFields } from './facilities-ui';
import { useToast } from './toast';
import { Alert, Button, Field, Input, Modal, Select, Textarea } from './ui';

type FormState = Record<
  | 'code'
  | 'name'
  | 'system'
  | 'status'
  | 'condition'
  | 'buildingId'
  | 'roomId'
  | 'locationDetail'
  | 'manufacturer'
  | 'model'
  | 'serialNumber'
  | 'vendor'
  | 'installedOn'
  | 'expectedLifeYears'
  | 'purchaseCost'
  | 'replacementCost'
  | 'warrantyExpiresOn'
  | 'notes',
  string
>;

function toForm(equipment?: Equipment | null): FormState {
  return {
    code: equipment?.code ?? '',
    name: equipment?.name ?? '',
    system: equipment?.system ?? 'OTHER',
    status: equipment?.status ?? 'IN_SERVICE',
    condition: equipment?.condition ?? 'GOOD',
    buildingId: equipment?.buildingId ?? '',
    roomId: equipment?.roomId ?? '',
    locationDetail: equipment?.locationDetail ?? '',
    manufacturer: equipment?.manufacturer ?? '',
    model: equipment?.model ?? '',
    serialNumber: equipment?.serialNumber ?? '',
    vendor: equipment?.vendor ?? '',
    installedOn: equipment?.installedOn?.slice(0, 10) ?? '',
    expectedLifeYears: equipment?.expectedLifeYears?.toString() ?? '',
    purchaseCost: equipment?.purchaseCost?.toString() ?? '',
    replacementCost: equipment?.replacementCost?.toString() ?? '',
    warrantyExpiresOn: equipment?.warrantyExpiresOn?.slice(0, 10) ?? '',
    notes: equipment?.notes ?? '',
  };
}

const text = (value: string) => value.trim() || null;

/** "$12,500.00" → 12500; blank → null; anything else → NaN. */
function parseAmount(value: string) {
  const cleaned = value.replace(/[$,\s]/g, '');
  if (!cleaned) return null;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? Math.round(amount * 100) / 100 : Number.NaN;
}

/** The request body, or a message saying what to fix. */
function toInput(form: FormState, editing: boolean): EquipmentInput | string {
  const lifeText = form.expectedLifeYears.trim();
  const expectedLifeYears = lifeText ? Number(lifeText) : null;
  if (expectedLifeYears !== null && (!Number.isInteger(expectedLifeYears) || expectedLifeYears < 1 || expectedLifeYears > 100)) {
    return 'Enter the expected life as a whole number of years, from 1 to 100';
  }
  const purchaseCost = parseAmount(form.purchaseCost);
  if (Number.isNaN(purchaseCost)) return 'Enter the purchase cost as an amount, like 12500';
  const replacementCost = parseAmount(form.replacementCost);
  if (Number.isNaN(replacementCost)) return 'Enter the replacement cost as an amount, like 15000';

  return {
    code: text(form.code),
    name: form.name.trim(),
    system: form.system as FacilitySystem,
    status: editing ? (form.status as EquipmentStatus) : 'IN_SERVICE',
    condition: form.condition as FacilityCondition,
    buildingId: form.buildingId || null,
    roomId: form.roomId || null,
    locationDetail: text(form.locationDetail),
    manufacturer: text(form.manufacturer),
    model: text(form.model),
    serialNumber: text(form.serialNumber),
    vendor: text(form.vendor),
    installedOn: form.installedOn || null,
    expectedLifeYears,
    purchaseCost,
    replacementCost,
    warrantyExpiresOn: form.warrantyExpiresOn || null,
    notes: text(form.notes),
  };
}

const SECTION_LABEL = 'mb-3 text-xs font-semibold tracking-wide text-slate-500 uppercase';

export function EquipmentFormModal({
  open,
  onClose,
  equipment,
  onSaved,
}: {
  open: boolean;
  onClose(): void;
  /** Edits this equipment; without it the form adds new equipment. */
  equipment?: Equipment | null;
  onSaved?(detail: EquipmentDetail): void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => toForm(equipment));
  const [error, setError] = useState('');
  const editing = !!equipment;

  useEffect(() => {
    if (open) {
      setForm(toForm(equipment));
      setError('');
    }
  }, [open, equipment]);

  const set = (key: keyof FormState) => (event: { target: { value: string } }) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const mutation = useMutation({
    mutationFn: (body: EquipmentInput) =>
      editing ? api.patch<EquipmentDetail>(`/facilities/assets/${equipment!.id}`, body) : api.post<EquipmentDetail>('/facilities/assets', body),
    onSuccess: (detail) => {
      queryClient.setQueryData(['facilities', 'equipment', detail.id], detail);
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success(editing ? `Saved ${equipmentLabel(detail)}` : `Added ${equipmentLabel(detail)}`);
      onSaved?.(detail);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const body = toInput(form, editing);
    if (typeof body === 'string') {
      setError(body);
      return;
    }
    setError('');
    mutation.mutate(body);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={editing ? `Edit ${equipmentLabel(equipment!)}` : 'Add equipment'}
      description={editing ? undefined : 'A building system or piece of equipment the facilities team maintains.'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="equipment-form" loading={mutation.isPending}>
            {editing ? 'Save changes' : 'Add equipment'}
          </Button>
        </>
      }
    >
      <form id="equipment-form" onSubmit={submit} className="space-y-6">
        {error && <Alert tone="red">{error}</Alert>}

        <section className="grid gap-4 md:grid-cols-2">
          <Field label="Code" hint="A short label, if you tag equipment">
            <Input value={form.code} onChange={set('code')} maxLength={40} placeholder="e.g. RTU-03" className="font-mono" />
          </Field>
          <Field label="Name" required>
            <Input value={form.name} onChange={set('name')} maxLength={150} required placeholder="e.g. Rooftop unit 3" />
          </Field>
          <Field label="System">
            <Select value={form.system} onChange={set('system')}>
              {(Object.keys(FACILITY_SYSTEM_LABEL) as FacilitySystem[]).map((value) => (
                <option key={value} value={value}>
                  {FACILITY_SYSTEM_LABEL[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Condition">
            <Select value={form.condition} onChange={set('condition')}>
              {(Object.keys(CONDITION_LABEL) as FacilityCondition[]).map((value) => (
                <option key={value} value={value}>
                  {CONDITION_LABEL[value]}
                </option>
              ))}
            </Select>
          </Field>
          {editing && (
            <Field label="Status">
              <Select value={form.status} onChange={set('status')}>
                {(Object.keys(EQUIPMENT_STATUS_LABEL) as EquipmentStatus[]).map((value) => (
                  <option key={value} value={value}>
                    {EQUIPMENT_STATUS_LABEL[value]}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </section>

        <section>
          <h3 className={SECTION_LABEL}>Location</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <PlaceFields buildingId={form.buildingId} roomId={form.roomId} onChange={(place) => setForm((current) => ({ ...current, ...place }))} />
            <Field label="Location detail" className="md:col-span-2">
              <Input value={form.locationDetail} onChange={set('locationDetail')} maxLength={200} placeholder="e.g. Roof, north side" />
            </Field>
          </div>
        </section>

        <section>
          <h3 className={SECTION_LABEL}>Make and model</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Manufacturer">
              <Input value={form.manufacturer} onChange={set('manufacturer')} maxLength={100} />
            </Field>
            <Field label="Model">
              <Input value={form.model} onChange={set('model')} maxLength={100} />
            </Field>
            <Field label="Serial number">
              <Input value={form.serialNumber} onChange={set('serialNumber')} maxLength={100} className="font-mono" />
            </Field>
            <Field label="Vendor" hint="Who installed or services it">
              <Input value={form.vendor} onChange={set('vendor')} maxLength={150} />
            </Field>
          </div>
        </section>

        <section>
          <h3 className={SECTION_LABEL}>Lifecycle and costs</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Installed on">
              <Input type="date" value={form.installedOn} onChange={set('installedOn')} />
            </Field>
            <Field label="Expected life (years)" hint="With the install date, this forecasts when to replace it">
              <Input type="number" inputMode="numeric" min={1} max={100} step={1} value={form.expectedLifeYears} onChange={set('expectedLifeYears')} placeholder="e.g. 20" />
            </Field>
            <Field label="Purchase cost">
              <Input inputMode="decimal" value={form.purchaseCost} onChange={set('purchaseCost')} placeholder="0.00" />
            </Field>
            <Field label="Replacement cost" hint="What replacing it would cost today">
              <Input inputMode="decimal" value={form.replacementCost} onChange={set('replacementCost')} placeholder="0.00" />
            </Field>
            <Field label="Warranty expires">
              <Input type="date" value={form.warrantyExpiresOn} onChange={set('warrantyExpiresOn')} />
            </Field>
          </div>
        </section>

        <Field label="Notes">
          <Textarea value={form.notes} onChange={set('notes')} maxLength={5000} rows={3} />
        </Field>
      </form>
    </Modal>
  );
}
